import { SearchIcon } from "@chakra-ui/icons";
import {
	BackgroundProps,
	Badge,
	Box,
	Button,
	Checkbox,
	Flex,
	HStack,
	Input,
	InputGroup,
	InputLeftElement,
	Show,
	Text,
	VStack,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useDebounce from "../../../hooks/useDebounce";
import useShiftKey from "../../../hooks/useShift";
import { DateOrString } from "../../../models/Job";
import { MatchingConsultant } from "../../../models/User";
import { useJob, useMatchingConsultants } from "../../../networking/job.hooks";
import { useOrganisation } from "../../../networking/organisation.hooks";
import { uniqArray } from "../../../utils/array.utils";
import { weekdaysToQuery } from "../../../utils/availability.utils";
import { displayCompetences } from "../../../utils/competence.utils";
import { decimalToPercentage } from "../../../utils/string.utils";
import AppIcon from "../../Core/AppIcon/AppIcon";
import AvailabilityHeader from "../AvailabilityHeader/AvailabilityHeader";
import CompetencePills from "../CompetencePills/CompetencePills";
import GuaranteedHoursCell from "../../GuaranteedHours/GuaranteedHoursCell";
import LoadingIndicator from "../LoadingIndicator/LoadingIndicator";
import Tag from "../Tag/Tag";
import WeeklyAvailability from "../WeeklyAvailability/WeeklyAvailability";

type InviteChangeEvent = {
	consultantIds: string[];
	addedConsultant?: string;
	removedConsultant?: string;
	isAll?: boolean;
};

export interface ConsultantSidebarProps {
	jobId: string;
	timeSlots?: { start: DateOrString; end: DateOrString }[];
	onSelectConsultant?: (consultant: MatchingConsultant, isAll?: boolean) => any;
	onCancelConsultant?: (consultant: MatchingConsultant) => any;
	onInviteConsultants?: (change: InviteChangeEvent) => any;
	invitedConsultants?: string[];
	declinedInvitations?: string[];
	bookedConsultant?: string;
	cancelledConsultant?: string;
}

const ConsultantsSidebar: React.FC<ConsultantSidebarProps> = ({
	jobId,
	timeSlots,
	onSelectConsultant,
	onInviteConsultants,
	onCancelConsultant,
	invitedConsultants,
	declinedInvitations,
	bookedConsultant,
	cancelledConsultant,
}) => {
	const { data: organisation } = useOrganisation();
	const { job } = useJob({ jobId });

	const [filterCustomerExperience, setFilterCustomerExperience] = useState(false);
	const [filterInvited, setFilterInvited] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const debouncedSearchTerm = useDebounce(searchTerm, 500);
	const [competenceFilter, setCompetenceFilter] = useState(job?.competence);
	const [filteredDays, setFilteredDays] = useState<number[]>([]);

	function handleAvailabilityFilter(i: number) {
		if (filteredDays.includes(i)) {
			setFilteredDays(filteredDays.filter((d) => d !== i));
		} else {
			setFilteredDays([...filteredDays, i]);
		}
	}

	const competences = organisation?.competences;

	const [showFilters, setShowFilters] = useState(false);

	const { t } = useTranslation();

	useEffect(() => {
		if (!competenceFilter && job?.competence) {
			setCompetenceFilter(job?.competence);
		}
	}, [job]);

	// Note: This is a bit of a hack to make sure that we don't show booked consultants when we have filters that overrides booked consultants
	// for example when filtering on Monday availability we should not show booked consultants if not available that day
	// TODO: Find out if this shouldn't be done on backend instead
	const includeBookedConsultants = !timeSlots?.length;

	const {
		data: users,
		isLoading,
		isFetching,
	} = useMatchingConsultants(
		{
			jobId,
			availability: organisation ? weekdaysToQuery(filteredDays) : undefined,
			branch: competenceFilter?.branch,
			service: competenceFilter?.service,
			skills: competenceFilter?.skills?.join(","),
			hasCustomerExperience: filterCustomerExperience + "",
			isInvited: filterInvited + "",
			keyword: debouncedSearchTerm.trim(),
			pageSize: 500,
			includeBookedConsultants: includeBookedConsultants,
			includePendingApplications: includeBookedConsultants,
			timeSlots: timeSlots
				?.map(
					(timeSlot) =>
						`${dayjs(timeSlot.start).toISOString()} - ${dayjs(timeSlot.end).toISOString()}`
				)
				.join(","),
			expand: "guaranteedHours",
		},
		{
			refetchOnWindowFocus: false,
		}
	);

	function toggleAnyCompetence() {
		if (competenceFilter?.branch === "any") {
			setCompetenceFilter(job?.competence);
		} else {
			setCompetenceFilter({ branch: "any", service: "any" });
			setShowFilters(false);
		}
	}

	return (
		<Flex align="stretch" flexDir="column" height={"100vh"} data-ignore-outside-click="true">
			<VStack
				px={4}
				pb={3}
				pt={10}
				spacing="3"
				align="stretch"
				flexShrink={0}
				bg="gray.100"
				borderBottom={"1px"}
				borderColor="gray.200"
			>
				<InputGroup>
					<InputLeftElement pointerEvents="none" children={<SearchIcon />} />
					<Input
						onChange={(e) => setSearchTerm(e.target.value)}
						placeholder={t("SEARCH")}
						bg="white"
						rounded={"full"}
					/>
				</InputGroup>

				<HStack spacing={5}>
					<HStack spacing={3}>
						<AppIcon icon="preference" size="16px" margin="0" />
						{competences && job?.competence && (
							<Text
								fontWeight="medium"
								textDecoration="underline"
								cursor="pointer"
								fontSize={"small"}
								onClick={() => setShowFilters(!showFilters)}
							>
								{displayCompetences(competences, competenceFilter)}
							</Text>
						)}
					</HStack>
					<HStack spacing={2}>
						<AppIcon icon="red-cross" size="11px" margin="0" onClick={toggleAnyCompetence} />
						<Text
							fontWeight="medium"
							color="danger"
							margin="0 0 0 0.2rem"
							cursor="pointer"
							fontSize="sm"
							onClick={toggleAnyCompetence}
						>
							{t(competenceFilter?.branch === "any" ? "job.matchJobsCompetence" : "CLEAR")}
						</Text>
					</HStack>
				</HStack>
				{showFilters && (
					<div className="competence-filters">
						{competences && (
							<CompetencePills
								value={competenceFilter}
								competences={competences}
								highlightedCompetence={job?.competence}
								onChange={(selectedCompetence) =>
									setCompetenceFilter({
										branch: selectedCompetence.branch,
										service: selectedCompetence.service || "",
										skills: selectedCompetence.skills,
									})
								}
								hideLabel
								hideBranch
							/>
						)}
					</div>
				)}
				<Flex justifyContent="space-between" alignItems="center">
					<Flex alignItems="center">
						<HStack
							alignItems="center"
							w={{ base: "190px", xl: "270px" }}
							spacing={{ base: 4, xl: 6 }}
						>
							<Checkbox
								isChecked={filterCustomerExperience}
								onChange={(e: any) => setFilterCustomerExperience(e.target.checked)}
								borderColor={"gray.500"}
							>
								{t("job.customerExperience")}
							</Checkbox>
							<Checkbox
								isChecked={filterInvited}
								onChange={(e: any) => setFilterInvited(e.target.checked)}
								borderColor={"gray.500"}
							>
								{t("INVITED")}
							</Checkbox>
						</HStack>
						<Box width={"200px"} hideBelow={"xl"}>
							<AvailabilityHeader onClick={handleAvailabilityFilter} selectedDays={filteredDays} />
						</Box>
					</Flex>

					{/* <Button
						size="sm"
						// textColor={"orange.500"}
						variant={"link"}
						onClick={handleSelectAllInvited}
					>
						Önska alla
					</Button> */}
				</Flex>
			</VStack>

			<Box px={2} py={2} overflowY={"scroll"}>
				{isLoading || isFetching ? (
					<Box p={8}>
						<LoadingIndicator size={"s"} showText={false} />
					</Box>
				) : (
					<ConsultantList
						consultants={users}
						// onSelectRow={selectRow}
						onBook={onSelectConsultant}
						onCancel={onCancelConsultant}
						onInvite={onInviteConsultants}
						invitedConsultants={invitedConsultants}
						declinedInvitations={declinedInvitations}
						bookedConsultant={bookedConsultant}
						cancelledConsultant={cancelledConsultant}
					/>
				)}
			</Box>
		</Flex>
	);
};

export default ConsultantsSidebar;

interface ConsultantListProps {
	consultants: MatchingConsultant[];
	invitedConsultants?: string[];
	declinedInvitations?: string[];
	bookedConsultant?: string;
	cancelledConsultant?: string;
	onBook?: (consultant: MatchingConsultant, bookAll?: boolean) => any;
	onInvite?: (change: InviteChangeEvent) => any;
	/**
	 * Called when the user cancels a booked consultant.
	 */
	onCancel?: (consultant: MatchingConsultant) => any;
}

const ConsultantList = React.memo<ConsultantListProps>(
	({
		consultants,
		onBook,
		onCancel,
		onInvite,
		invitedConsultants: oInvitedConsultants,
		declinedInvitations,
		bookedConsultant,
	}) => {
		const { t } = useTranslation();

		const [isShiftDown, setIsShiftDown] = useState(false);

		const [invitedConsultants, setInvitedConsultant] = useState<string[]>(
			oInvitedConsultants || []
		);

		function handleInviteConsultant(consultantId: string) {
			if (onInvite) {
				if (invitedConsultants.includes(consultantId)) {
					const updatedInvitedConsultants = invitedConsultants.filter((id) => id !== consultantId);
					setInvitedConsultant(updatedInvitedConsultants);
					onInvite({
						consultantIds: updatedInvitedConsultants,
						removedConsultant: consultantId,
						// isAll: isShiftDown,
					});
				} else {
					if (
						bookedConsultant &&
						!window.confirm(
							"En konsult är redan vald som bokad, denne kommer bli avbokad när du önskar konsult(er) på samma pass. Vill du fortsätta?"
						)
					) {
						return;
					}

					const updatedInvitedConsultants = uniqArray([...invitedConsultants, consultantId]);
					setInvitedConsultant(updatedInvitedConsultants);
					onInvite({
						consultantIds: updatedInvitedConsultants,
						// isAll: isShiftDown,
						addedConsultant: consultantId,
					});
				}
			}
		}

		function handleBookConsultant(consultant: MatchingConsultant) {
			if (onBook) {
				onBook(consultant, isShiftDown);
				// Clear local state of invited consultants as they will be cleared when another is booked
				setInvitedConsultant([]);
			}
		}

		function handleUnbookConsultant(consultant: MatchingConsultant) {
			if (onCancel) {
				onCancel(consultant);
			}
		}

		const sortedConsultants = useMemo(
			() =>
				consultants.sort((a, b) => {
					if (a.id === bookedConsultant) return -1;
					if (b.id === bookedConsultant) return 1;
					if (a.isBooked && !b.isBooked) return -1;
					if (!a.isBooked && b.isBooked) return 1;
					if (a.isInvited && !b.isInvited) return -1;
					if (a.hasCustomerExperience && !b.hasCustomerExperience) return -1;
					if (!a.hasCustomerExperience && b.hasCustomerExperience) return 1;
					if (a.hasPendingApplication && !b.hasPendingApplication) return -1;
					if (!a.hasPendingApplication && b.hasPendingApplication) return 1;
					return 0;
				}),
			[consultants]
		);

		useShiftKey(setIsShiftDown);

		function getInviteButtonText(consultant: MatchingConsultant) {
			const isInvited = invitedConsultants.includes(consultant.id);

			// if (isShiftDown) {
			// 	return isInvited ? t("job.action.uninviteAll") : t("job.action.inviteAll");
			// }
			if (isInvited) {
				return t("job.invited");
			}
			if (declinedInvitations?.includes(consultant.id)) {
				return t("job.inviteAgain");
			}
			return t("job.invite");
		}

		return (
			<>
				{sortedConsultants.map((consultant) => {
					const isInvited = invitedConsultants.includes(consultant.id);
					const isBooked = consultant.id === bookedConsultant;
					const hasDeclinedInvitation = declinedInvitations?.includes(consultant.id);
					const hasApplied = consultant.hasPendingApplication;

					let bg: BackgroundProps["bg"] = "white";

					if (consultant.blocked) {
						bg = "red.100";
					} else if (hasDeclinedInvitation) {
						bg = "red.50";
					} else if (isInvited) {
						bg = "orange.50";
					} else if (hasApplied) {
						bg = "blue.50";
					}

					return (
						<VStack
							key={consultant.id}
							className={`${consultant.blocked && "warning"}`}
							data-contact={consultant.id}
							alignItems="stretch"
							spacing={1.5}
							borderBottomStyle="solid"
							borderBottomWidth={1}
							borderBottomColor="gray.200"
							py={3}
							px={2}
							bg={bg}
							_hover={{
								bg:
									isInvited || consultant.blocked || hasDeclinedInvitation || hasApplied
										? undefined
										: "gray.50",
							}}
						>
							<Flex justifyContent={"space-between"} align="center">
								<Flex>
									<HStack spacing={4}>
										<VStack align={"flex-start"} w={{ base: "200px", xl: "280px" }} pr="10px">
											<HStack alignItems={"center"}>
												<Text
													color="actionBlue"
													data-contact={consultant.id}
													lineHeight={1}
													fontSize="md"
												>
													{consultant.firstName} {consultant.lastName}
												</Text>

												{consultant.hasCustomerExperience && (
													<Tag text={t("job.customerExperienceShort")} color="orange" size="s" />
												)}

												{consultant.id === bookedConsultant && (
													<Tag text={t("job.isBooked")} color="green" size="s" />
												)}

												{consultant.blocked && <Tag text={"Blockerad"} color="danger" size="s" />}
											</HStack>

											<Show below="xl">
												<WeeklyAvailability
													hideWeekdays
													condensed
													availability={consultant?.profile?.availability}
												/>
											</Show>

											<HStack spacing="4">
												<Text fontSize="sm">{consultant.profile?.address?.city || ""}</Text>

												<Badge
													fontSize={"11px"}
													textColor={"gray.900"}
													bg={consultant.tg && consultant.tg < 0.3 ? "red.200" : undefined}
												>
													TG {decimalToPercentage(consultant.tg)}
												</Badge>

												<GuaranteedHoursCell guaranteedHours={consultant.guaranteedHours} />
											</HStack>
										</VStack>
									</HStack>
									<Show above="xl">
										<Flex w={"200px"} alignItems={"center"}>
											<WeeklyAvailability
												hideWeekdays
												condensed
												availability={consultant?.profile?.availability}
											/>
										</Flex>
									</Show>
								</Flex>

								<VStack alignItems="flex-end">
									<HStack>
										{!isBooked && (
											<Button
												backgroundColor={isInvited ? "orange.300" : undefined}
												size="xs"
												colorScheme={"orange"}
												fontWeight={"medium"}
												onClick={() => handleInviteConsultant(consultant.id)}
												isDisabled={consultant.blocked}
											>
												{getInviteButtonText(consultant)}
											</Button>
										)}
										{!isBooked ? (
											<Button
												colorScheme="green"
												size="xs"
												fontWeight={"medium"}
												onClick={() => handleBookConsultant(consultant)}
											>
												{isShiftDown ? t("job.action.bookAll") : t("job.action.book")}
											</Button>
										) : (
											<Button
												colorScheme="gray"
												textColor={"red"}
												size="xs"
												fontWeight={"medium"}
												onClick={() => handleUnbookConsultant(consultant)}
											>
												{t("job.action.cancelApplication")}
											</Button>
										)}
									</HStack>
									{hasDeclinedInvitation && (
										<Text fontSize="2xs" mt="-1">
											{t("job.inviteDeclined")}
										</Text>
									)}
									{hasApplied && (
										<Text fontSize="2xs" mt="-1">
											{t("job.hasApplied")}
										</Text>
									)}
								</VStack>
							</Flex>
						</VStack>
					);
				})}
			</>
		);
	}
);
